import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, Plus, Calendar, Clock,
  User, Video, MapPin, RefreshCw, Loader2, X
} from 'lucide-react';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  format, isSameMonth, isSameDay, isToday, addMonths, subMonths,
  addWeeks, subWeeks, parseISO, getHours, getMinutes, setHours, setMinutes,
  startOfDay, endOfDay
} from 'date-fns';
import toast from 'react-hot-toast';

const API_BASE    = process.env.REACT_APP_API_BASE    || 'https://api.abenaihr.com';
const API_KEY     = process.env.REACT_APP_API_KEY     || '';
const PROVIDER_ID = process.env.REACT_APP_PROVIDER_ID || localStorage.getItem('abena_provider_id') || 'PROVIDER-001';
const PRACTICE_ID = process.env.REACT_APP_PRACTICE_ID || 'ABENA-001';

const TYPE_CONFIG = {
  telehealth:  { color: 'bg-blue-100 text-blue-800 border-blue-200',   dot: 'bg-blue-500',   icon: Video,    label: 'Telehealth' },
  follow_up:   { color: 'bg-purple-100 text-purple-800 border-purple-200', dot: 'bg-purple-500', icon: User, label: 'Follow-up' },
  new_patient: { color: 'bg-green-100 text-green-800 border-green-200',  dot: 'bg-green-500',  icon: User,    label: 'New Patient' },
  annual:      { color: 'bg-amber-100 text-amber-800 border-amber-200',  dot: 'bg-amber-500',  icon: Calendar,label: 'Annual Exam' },
  urgent:      { color: 'bg-red-100 text-red-800 border-red-200',        dot: 'bg-red-500',    icon: User,    label: 'Urgent' },
  default:     { color: 'bg-gray-100 text-gray-800 border-gray-200',     dot: 'bg-gray-400',   icon: Calendar,label: 'Appointment' },
};
const typeOf = t => TYPE_CONFIG[t] || TYPE_CONFIG.default;

// ── Quick-book modal (inline) ──────────────────────────────────────────────
const BookModal = ({ date, onClose, onBooked }) => {
  const [form, setForm] = useState({
    visitType: 'follow_up', time: '09:00', duration: 30,
    patientId: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.patientId || !form.time) { toast.error('Patient ID and time required'); return; }
    setSaving(true);
    try {
      const startISO = `${format(date, 'yyyy-MM-dd')}T${form.time}:00Z`;
      const resp = await fetch(`${API_BASE}/schedule/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY, 'X-ABENA-Provider-Id': PROVIDER_ID },
        body: JSON.stringify({
          practice_id: PRACTICE_ID, provider_id: PROVIDER_ID,
          patient_id: form.patientId, start_time: startISO,
          duration_minutes: form.duration, appointment_type: form.visitType,
          notes_patient: form.notes || null,
        }),
      });
      if (!resp.ok) throw new Error((await resp.json()).detail || 'Booking failed');
      toast.success('Appointment booked');
      onBooked();
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.5)'}}>
      <motion.div initial={{scale:0.9,opacity:0}} animate={{scale:1,opacity:1}}
                  className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Book Appointment</h3>
            <p className="text-sm text-gray-500">{format(date, 'EEEE, MMMM d, yyyy')}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5"/></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Visit Type</label>
            <div className="flex gap-2 flex-wrap">
              {[['follow_up','Follow-up'],['telehealth','Telehealth'],['new_patient','New Patient'],['annual','Annual'],['urgent','Urgent']].map(([v,l])=>(
                <button key={v} onClick={()=>set('visitType',v)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${form.visitType===v ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
              <input type="time" value={form.time} onChange={e=>set('time',e.target.value)}
                     className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <select value={form.duration} onChange={e=>set('duration',Number(e.target.value))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                {[15,30,45,60,90].map(m=><option key={m} value={m}>{m} min</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID</label>
            <input value={form.patientId} onChange={e=>set('patientId',e.target.value)}
                   placeholder="e.g. JANE_001"
                   className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e=>set('notes',e.target.value)} rows={2}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"/>
          </div>
          <button onClick={submit} disabled={saving}
                  className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 text-white text-sm font-semibold flex items-center justify-center gap-2">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin"/>Booking...</> : 'Book Appointment'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ── Appointment detail popover ─────────────────────────────────────────────
const ApptPopover = ({ appt, onClose }) => {
  if (!appt) return null;
  const cfg = typeOf(appt.appointment_type);
  const Icon = cfg.icon;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.4)'}}>
      <motion.div initial={{scale:0.9,opacity:0}} animate={{scale:1,opacity:1}}
                  className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5">
        <div className="flex items-start justify-between mb-3">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${cfg.color}`}>
            <Icon className="w-3.5 h-3.5"/> {cfg.label}
          </span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4"/></button>
        </div>
        <p className="font-semibold text-gray-900 mb-3">Patient: {appt.patient_id}</p>
        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400"/>
            {format(parseISO(appt.start_time), 'h:mm a')} · {appt.duration_minutes} min
          </div>
          {appt.location && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400"/> {appt.location}
            </div>
          )}
          {appt.notes_patient && (
            <p className="text-xs bg-gray-50 rounded-lg p-2 mt-2">{appt.notes_patient}</p>
          )}
        </div>
        <div className="mt-3 flex gap-2">
          <span className={`text-xs px-2 py-1 rounded-lg font-medium ${
            appt.status === 'confirmed' ? 'bg-green-100 text-green-700' :
            appt.status === 'cancelled' ? 'bg-red-100 text-red-700' :
            'bg-yellow-100 text-yellow-700'
          }`}>
            {appt.status || 'scheduled'}
          </span>
        </div>
      </motion.div>
    </div>
  );
};

// ── Month cell ────────────────────────────────────────────────────────────
const MonthDay = ({ day, appointments, currentMonth, onBook, onSelect }) => {
  const isCurrentMonth = isSameMonth(day, currentMonth);
  const isT = isToday(day);
  const dayAppts = appointments.filter(a => {
    try { return isSameDay(parseISO(a.start_time), day); } catch { return false; }
  });
  return (
    <div
      className={`min-h-[90px] p-1.5 border-b border-r border-gray-100 cursor-pointer group hover:bg-blue-50 transition-colors ${!isCurrentMonth ? 'bg-gray-50' : ''}`}
      onClick={() => onBook(day)}>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full ${
          isT ? 'bg-blue-600 text-white' : isCurrentMonth ? 'text-gray-700' : 'text-gray-300'
        }`}>{format(day, 'd')}</span>
        {dayAppts.length > 0 && (
          <span className="text-xs text-gray-400 opacity-0 group-hover:opacity-100">+ Book</span>
        )}
      </div>
      <div className="space-y-0.5">
        {dayAppts.slice(0,3).map((a,i) => {
          const cfg = typeOf(a.appointment_type);
          return (
            <div key={i}
                 onClick={e=>{ e.stopPropagation(); onSelect(a); }}
                 className={`text-xs px-1.5 py-0.5 rounded-md truncate border cursor-pointer hover:opacity-80 ${cfg.color}`}>
              <span className="font-medium">{format(parseISO(a.start_time),'h:mm a')}</span>
              {' '}{a.patient_id}
            </div>
          );
        })}
        {dayAppts.length > 3 && (
          <div className="text-xs text-blue-600 pl-1">+{dayAppts.length - 3} more</div>
        )}
      </div>
    </div>
  );
};

// ── Week timeline ────────────────────────────────────────────────────────
const HOURS = Array.from({length:13},(_, i)=>i+7); // 7am–7pm
const WeekView = ({ weekStart, appointments, onBook, onSelect }) => {
  const days = eachDayOfInterval({ start: weekStart, end: endOfWeek(weekStart, {weekStartsOn:0}) });
  return (
    <div className="overflow-auto">
      {/* Header row */}
      <div className="grid grid-cols-8 border-b border-gray-100">
        <div className="p-2 text-xs text-gray-400 text-center">Time</div>
        {days.map(d=>(
          <div key={d} className={`p-2 text-center ${isToday(d) ? 'bg-blue-50' : ''}`}>
            <p className="text-xs text-gray-500">{format(d,'EEE')}</p>
            <p className={`text-sm font-semibold ${isToday(d)?'text-blue-600':'text-gray-800'}`}>{format(d,'d')}</p>
          </div>
        ))}
      </div>
      {/* Time grid */}
      {HOURS.map(hour=>(
        <div key={hour} className="grid grid-cols-8 border-b border-gray-50 min-h-[52px]">
          <div className="p-1.5 text-xs text-gray-300 text-right pr-3 border-r border-gray-100 leading-none pt-2">
            {hour % 12 || 12}{hour < 12 ? 'a' : 'p'}
          </div>
          {days.map(d=>{
            const cellAppts = appointments.filter(a=>{
              try {
                const dt = parseISO(a.start_time);
                return isSameDay(dt, d) && getHours(dt) === hour;
              } catch { return false; }
            });
            return (
              <div key={d} className={`relative border-r border-gray-50 p-0.5 cursor-pointer hover:bg-blue-50 group ${isToday(d)?'bg-blue-50/30':''}`}
                   onClick={()=>onBook(setHours(setMinutes(d,0),hour))}>
                {cellAppts.map((a,i)=>{
                  const cfg = typeOf(a.appointment_type);
                  return (
                    <div key={i}
                         onClick={e=>{e.stopPropagation();onSelect(a);}}
                         className={`text-xs px-1 py-0.5 rounded truncate border mb-0.5 cursor-pointer hover:opacity-80 ${cfg.color}`}>
                      {a.patient_id}
                    </div>
                  );
                })}
                <span className="absolute inset-0 flex items-center justify-center text-xs text-blue-400 opacity-0 group-hover:opacity-100">+</span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// ── Main Calendar ────────────────────────────────────────────────────────
const ProviderCalendar = () => {
  const [view, setView] = useState('month'); // 'month' | 'week'
  const [current, setCurrent] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookDay, setBookDay] = useState(null);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [lastSync, setLastSync] = useState(null);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      // Determine date window based on view
      const rangeStart = view === 'month'
        ? startOfWeek(startOfMonth(current), {weekStartsOn:0})
        : startOfWeek(current, {weekStartsOn:0});
      const rangeEnd = view === 'month'
        ? endOfWeek(endOfMonth(current), {weekStartsOn:0})
        : endOfWeek(current, {weekStartsOn:0});

      const params = new URLSearchParams({
        provider_id: PROVIDER_ID,
        start_date: format(rangeStart, 'yyyy-MM-dd'),
        end_date:   format(rangeEnd,   'yyyy-MM-dd'),
      });
      const resp = await fetch(`${API_BASE}/schedule/appointments/${PRACTICE_ID}?${params}`, {
        headers: { 'X-API-Key': API_KEY, 'X-ABENA-Provider-Id': PROVIDER_ID },
      });
      if (resp.ok) {
        const d = await resp.json();
        setAppointments(Array.isArray(d) ? d : (d.appointments || []));
      }
    } catch (e) {
      // silently keep stale data
    } finally {
      setLoading(false);
      setLastSync(new Date());
    }
  }, [view, current]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  // Auto-refresh every 60 s
  useEffect(() => {
    const t = setInterval(fetchAppointments, 60_000);
    return () => clearInterval(t);
  }, [fetchAppointments]);

  const prev = () => view === 'month' ? setCurrent(c => subMonths(c,1)) : setCurrent(c => subWeeks(c,1));
  const next = () => view === 'month' ? setCurrent(c => addMonths(c,1)) : setCurrent(c => addWeeks(c,1));

  const monthDays = eachDayOfInterval({
    start: startOfWeek(startOfMonth(current), {weekStartsOn:0}),
    end:   endOfWeek(endOfMonth(current),     {weekStartsOn:0}),
  });
  const weekStart = startOfWeek(current, {weekStartsOn:0});

  const todayCount   = appointments.filter(a=>{try{return isSameDay(parseISO(a.start_time),new Date());}catch{return false;}}).length;
  const telehealthCount = appointments.filter(a=>a.appointment_type==='telehealth').length;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Appointments sync across provider, admin, and patient portals
            {lastSync && <span className="ml-2 text-gray-300">· synced {format(lastSync,'h:mm a')}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchAppointments} disabled={loading}
                  className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-500">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}/>
          </button>
          <button onClick={() => setBookDay(new Date())}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl">
            <Plus className="w-4 h-4"/> Book Appointment
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { label: "Today's Appointments", value: todayCount, color: 'text-blue-700' },
          { label: 'This Period Total',    value: appointments.length, color: 'text-purple-700' },
          { label: 'Telehealth',           value: telehealthCount, color: 'text-indigo-700' },
        ].map(s=>(
          <div key={s.label} className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Calendar card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <button onClick={prev} className="p-1.5 rounded-lg hover:bg-gray-100">
              <ChevronLeft className="w-5 h-5 text-gray-600"/>
            </button>
            <h2 className="text-sm font-semibold text-gray-800 min-w-[160px] text-center">
              {view === 'month' ? format(current,'MMMM yyyy') : `Week of ${format(weekStart,'MMM d, yyyy')}`}
            </h2>
            <button onClick={next} className="p-1.5 rounded-lg hover:bg-gray-100">
              <ChevronRight className="w-5 h-5 text-gray-600"/>
            </button>
            <button onClick={()=>{setCurrent(new Date());}}
                    className="ml-1 text-xs px-2.5 py-1 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600">
              Today
            </button>
          </div>
          {/* View toggle */}
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            {[['month','Month'],['week','Week']].map(([v,l])=>(
              <button key={v} onClick={()=>setView(v)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${view===v?'bg-white shadow-sm text-blue-700':'text-gray-600 hover:text-gray-900'}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Day-of-week header (month view) */}
        {view === 'month' && (
          <div className="grid grid-cols-7 border-b border-gray-100">
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d=>(
              <div key={d} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">{d}</div>
            ))}
          </div>
        )}

        {loading && appointments.length === 0 ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin"/>
          </div>
        ) : view === 'month' ? (
          <div className="grid grid-cols-7">
            {monthDays.map(day=>(
              <MonthDay key={day.toISOString()} day={day} appointments={appointments}
                        currentMonth={current} onBook={setBookDay} onSelect={setSelectedAppt}/>
            ))}
          </div>
        ) : (
          <WeekView weekStart={weekStart} appointments={appointments}
                    onBook={setBookDay} onSelect={setSelectedAppt}/>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-4">
        {Object.entries(TYPE_CONFIG).filter(([k])=>k!=='default').map(([k,cfg])=>(
          <div key={k} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`}/>
            <span className="text-xs text-gray-500">{cfg.label}</span>
          </div>
        ))}
      </div>

      {/* Modals */}
      {bookDay && (
        <BookModal date={bookDay} onClose={()=>setBookDay(null)} onBooked={fetchAppointments}/>
      )}
      {selectedAppt && (
        <ApptPopover appt={selectedAppt} onClose={()=>setSelectedAppt(null)}/>
      )}
    </div>
  );
};

export default ProviderCalendar;
